import React, { useCallback, useMemo } from "react";

import { useFetch } from "2g-hooks/fetch";
import { Theme } from "2g-widget";
import { Icons } from "2g-icons";
import { getFetcherForMethod } from "2g-utils-ajax";
import { useNotificationSuccess } from "2g-notifications";

import { DATA } from "b2b/api";
import useMessages from "./i18n/useMessages";

import { NOTIFIKACE_PATCH_ZMENA } from "b2b/constants";
import {
  useRedirectToZadostDetail,
  useRedirectToProvozniInformaceDetail
} from "b2b/redirects";

export default function useNotifikaceWidget({
  notifikace = [],
  onClose,
  onSuccess
}) {
  const fetch = useFetch();
  const messageSucces = useNotificationSuccess();
  const messages = useMessages();

  const idNotifikaceAllDelete = useMemo(
    () => notifikace.map(item => item.id),
    [notifikace]
  );
  const idNotifikaceAllRead = useMemo(
    () => notifikace.filter(item => !item.prectena).map(item => item.id),
    [notifikace]
  );

  const redirectToDetailZadost = useRedirectToZadostDetail();
  const redirectToDetailprovozniInformace =
    useRedirectToProvozniInformaceDetail();

  const handleSuccess = (zmena, idNotifikace) => () => {
    messageSucces(
      zmena == NOTIFIKACE_PATCH_ZMENA.DELETE
        ? messages.successDelete({ length: idNotifikace.length })
        : messages.successRead({ length: idNotifikace.length })
    );
    onSuccess && onSuccess();
    if (zmena == NOTIFIKACE_PATCH_ZMENA.DELETE) {
      onClose();
    }
  };

  const load = useCallback(
    (zmena, idNotifikace) => {
      fetch({
        url: DATA.notifikace(),
        payload: {
          zmena,
          idNotifikace
        },
        fetcher: getFetcherForMethod("patch"),
        onSuccess: handleSuccess(zmena, idNotifikace)
      });
    },
    [fetch]
  );

  const handleReadAll = useCallback(() => {
    load(NOTIFIKACE_PATCH_ZMENA.READ, idNotifikaceAllRead);
  }, [load, idNotifikaceAllRead]);

  const handleDeleteAll = useCallback(() => {
    load(NOTIFIKACE_PATCH_ZMENA.DELETE, idNotifikaceAllDelete);
  }, [load, idNotifikaceAllDelete]);

  const handleRead = useCallback(
    notifikace => {
      load(NOTIFIKACE_PATCH_ZMENA.READ, [notifikace.id]);
    },
    [load]
  );

  const handleClick = useCallback(
    notifikace => {
      onClose();
      !notifikace.prectena && handleRead(notifikace);
      if (notifikace.idZadost) {
        redirectToDetailZadost(notifikace.idZadost);
      } else if (
        notifikace.idProvozniInformace &&
        ["PROVOZNI_INFORMACE_PRIDANI", "PROVOZNI_INFORMACE_ZMENA"].includes(
          notifikace.typ?.name
        )
      ) {
        redirectToDetailprovozniInformace(notifikace.idProvozniInformace);
      }
    },
    [
      onClose,
      handleRead,
      redirectToDetailZadost,
      redirectToDetailprovozniInformace
    ]
  );

  const handleDelete = useCallback(
    notifikace => {
      load(NOTIFIKACE_PATCH_ZMENA.DELETE, [notifikace.id]);
    },
    [load]
  );

  const headerActions = useMemo(() => {
    const actions = [
      <Theme.HeaderAction key="close" onClick={onClose}>
        <Icons.Close />
      </Theme.HeaderAction>
    ];

    if (notifikace.length) {
      actions.unshift(
        <Theme.HeaderAction
          key="readAll"
          disabled={notifikace.every(n => n.prectena)}
          onClick={handleReadAll}
        >
          <Icons.CheckDouble />
          <span className="b2b-NotifikaceActionLabel">
            {messages.readAll()}
          </span>
        </Theme.HeaderAction>,
        <Theme.HeaderAction key="deleteAll" onClick={handleDeleteAll}>
          <Icons.DeleteCross />
          <span className="b2b-NotifikaceActionLabel">
            {messages.deleteAll()}
          </span>
        </Theme.HeaderAction>
      );
    }

    return actions;
  }, [onClose, notifikace, handleReadAll, handleDeleteAll]);

  return {
    headerActions,
    onClick: handleClick,
    onRead: handleRead,
    onDelete: handleDelete
  };
}
